import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useSnackbar } from "notistack";
import { useQuery } from "react-query";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import axios from "axios-config";

import { useTranslation } from "react-i18next";
import { useApp } from "contexts/AppContext";
import InviteUserForm from "./InviteUserForm";
import StyledAvatar from "components/StyledAvatar/StyledAvatar";
import {
  fetchUsers,
  fetchUserInvitations,
  cancelUserInvitation,
} from "api/organizations";
import SettingsHeader from "./SettingsHeader";
import dayjs from "dayjs";

const UsersSettings = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Dialog open/close
  const [userInvitationDialogOpen, setUserInvitationDialogOpen] =
    useState(false);

  // For pending invites (if any)
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuInvitationId, setMenuInvitationId] = useState(null);
  const [cancelingInvitationId, setCancelingInvitationId] = useState(null);

  // Query: fetch organization users
  const { data: users, isLoading } = useQuery("organiaztionUsers", fetchUsers, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
  const { data: invitations, refetch: refetchInvitations } = useQuery(
    "userInvitations",
    fetchUserInvitations,
    {
      refetchOnWindowFocus: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
    }
  );

  const handleDialogClose = () => {
    setUserInvitationDialogOpen(false);
  };

  // Called when an invitation is successfully sent in InviteUserForm
  const onInvitationSent = () => {
    handleDialogClose();
    refetchInvitations(); // Ensure invitations list is updated after sending
  };

  // Cancel invitation
  const handleInvitationCancel = async (invitationId) => {
    setCancelingInvitationId(invitationId);
    try {
      const response = await cancelUserInvitation(invitationId);
      if (response.status === 200) {
        enqueueSnackbar(t("Invitation canceled."), { variant: "success" });
        refetchInvitations();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setCancelingInvitationId(null);
    }
  };

  // Resend invitation
  const handleInvitationResend = async (invitationId) => {
    try {
      const response = await axios.patch(
        `/organizations/inviteUser/${invitationId}`
      );
      if (response.status === 200) {
        enqueueSnackbar(t("Invitation sent successfully!"), {
          variant: "success",
        });
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
      }
    }
  };
  const handleMenuOpen = (event, invitationId) => {
    setAnchorEl(event.currentTarget);
    setMenuInvitationId(invitationId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuInvitationId(null);
  };
  if (isLoading) {
    // Replace with your own loader/spinner or skeleton
    return null;
  }

  console.log(invitations);

  return (
    <>
      <Box className={"container mainContainer docsContainer "}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "#fff", // updated to white background
            py: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            <div className="headerPush"></div>
            <SettingsHeader title={t("settings.users.title")} />

            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                {/* Members Table */}
                <Stack
                  direction={"row"}
                  alignItems="center"
                  justifyContent={"space-between"}
                  spacing={1}
                  mb={2}
                >
                  <Typography variant="h3" sx={{ mb: 2 }}>
                    {t("Members")}
                  </Typography>
                  {!process.env.REACT_APP_COSTI_LOCK && (
                    <Button
                      startIcon={<PersonAddIcon />}
                      variant="contained"
                      onClick={() => setUserInvitationDialogOpen(true)}
                    >
                      {t("Invite People")}
                    </Button>
                  )}
                </Stack>
                <TableContainer variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("Name")}</TableCell>
                        <TableCell>{t("Email")}</TableCell>
                        <TableCell>{t("settings.users.dateAdded")}</TableCell>
                        <TableCell align="right">{t("Action")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => {
                        // Format the date if needed, e.g. to locale string
                        const dateAdded = user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : t("N/A");

                        return (
                          <TableRow key={user.id} hover>
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                {/* Custom avatar */}
                                <StyledAvatar
                                  name={user.name}
                                  userId={user.id}
                                  alt={user.name}
                                  src="/static/images/avatar/1.jpg"
                                  size={32}
                                />
                                <Typography variant="body2">
                                  {user.name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{dateAdded}</TableCell>
                            <TableCell align="right">
                              {/* Example placeholder action buttons */}
                              {/* <IconButton size="small">
                                <MoreHorizRoundedIcon />
                              </IconButton> */}
                              {/* Could be a remove user, etc. */}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {invitations?.map((invitation) => (
                        <TableRow key={invitation.id} hover>
                          <TableCell>
                            <Typography variant="body2" color={grey[600]}>
                              {t("settings.users.userInvitationPending")}
                            </Typography>
                          </TableCell>
                          <TableCell>{invitation.inviteeEmail}</TableCell>
                          <TableCell>
                            {dayjs(invitation.createdAt).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, invitation.id)}
                              disabled={cancelingInvitationId === invitation.id}
                            >
                              <MoreHorizRoundedIcon />
                            </IconButton>
                            {/* Only show one menu, anchored to the correct row */}
                            <Menu
                              anchorEl={anchorEl}
                              open={
                                Boolean(anchorEl) &&
                                menuInvitationId === invitation.id
                              }
                              onClose={handleMenuClose}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              <MenuItem
                                onClick={() => {
                                  handleInvitationCancel(invitation.id);
                                  handleMenuClose();
                                }}
                                sx={{ color: "error.main" }}
                                disabled={
                                  cancelingInvitationId === invitation.id
                                }
                              >
                                {cancelingInvitationId === invitation.id ? (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <CircularProgress size={16} />
                                    <span>{t("Canceling...")}</span>
                                  </Stack>
                                ) : (
                                  t("Cancel")
                                )}
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Container>
        </Box>
      </Box>

      {/* Invite Dialog */}
      <Dialog
        open={userInvitationDialogOpen}
        fullWidth
        maxWidth="sm"
        onClose={handleDialogClose}
      >
        <DialogTitle>{t("Invite people to organization")}</DialogTitle>
        <InviteUserForm onInvitationSent={onInvitationSent} />
      </Dialog>
    </>
  );
};

export default UsersSettings;
